/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Render the modal layer and popover positioning as React portals.
 * @state-owner overlay-local React state
 * @allowed-invariants popover clamps to the viewport exactly like openPop()
 * @allowed-imports React
 * @forbidden business rules; network; navigation decisions
 * @public-api workspace shell and modules
 * @side-effects renders fixed-position overlay DOM and listens for document clicks
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface PopoverRequest {
  readonly content: ReactNode;
  readonly x: number;
  readonly y: number;
  readonly className?: string;
}

interface OverlayContextValue {
  openModal(content: ReactNode): void;
  closeModal(): void;
  openPopover(request: PopoverRequest): void;
  closePopover(): void;
  isModalOpen: boolean;
  isPopoverOpen: boolean;
}

const OverlayContext = createContext<OverlayContextValue | null>(null);

export function useOverlay(): OverlayContextValue {
  const value = useContext(OverlayContext);
  if (!value) throw new Error('OverlayHost is missing');
  return value;
}

/** The anchor point for a popover, falling back when the anchor has been unmounted. */
export function anchorPoint(event: { currentTarget: Element | null }): { x: number; y: number } {
  const rect = event.currentTarget?.getBoundingClientRect();
  if (rect && (rect.width || rect.height)) return { x: rect.left, y: rect.bottom + 8 };
  return { x: window.innerWidth / 2 - 140, y: 160 };
}

/** The anchoring used by the rail popovers. */
export function railAnchor(
  event: { currentTarget: Element | null },
  offsetTop: number,
): { x: number; y: number } {
  const rect = event.currentTarget?.getBoundingClientRect();
  const mobile = window.innerWidth <= 900;
  if (!rect) return { x: 14, y: 0 };
  return mobile ? { x: 14, y: 0 } : { x: rect.right + 10, y: rect.top - offsetTop };
}

export function pointerPoint(event: { clientX: number; clientY: number }): {
  x: number;
  y: number;
} {
  if (event.clientX || event.clientY) return { x: event.clientX, y: event.clientY };
  return { x: window.innerWidth / 2 - 140, y: 160 };
}

export function OverlayHost({ children }: PropsWithChildren) {
  /**
   * A stack, not a slot: a dialog opened from inside another (a guide, a dispatch form) must lay
   * over it and hand control back when it closes, rather than replacing what the teacher was on.
   */
  const [modals, setModals] = useState<readonly ReactNode[]>([]);
  const modal = modals[modals.length - 1] ?? null;
  const [popover, setPopover] = useState<PopoverRequest | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  const closePopover = useCallback(() => {
    setPopover(null);
    setPosition(null);
  }, []);

  const openPopover = useCallback((request: PopoverRequest) => {
    setPosition(null);
    setPopover(request);
  }, []);

  const closeModal = useCallback(() => setModals((stack) => stack.slice(0, -1)), []);
  const openModal = useCallback(
    (content: ReactNode) => setModals((stack) => [...stack, content]),
    [],
  );

  /**
   * Clamp the popover inside the viewport. Re-run whenever it resizes — late-loading
   * fonts change the measured height after the first pass, and a stale measurement would
   * push the popover past the viewport edge.
   */
  useLayoutEffect(() => {
    if (!popover) return;
    const node = popoverRef.current;
    if (!node) return;

    const place = () => {
      const rect = node.getBoundingClientRect();
      let left = popover.x;
      const top = Math.min(popover.y, window.innerHeight - rect.height - 14);
      if (left + rect.width > window.innerWidth - 14) left = window.innerWidth - rect.width - 14;
      setPosition((current) => {
        const next = { left: Math.max(14, left), top: Math.max(14, top) };
        return current && current.left === next.left && current.top === next.top ? current : next;
      });
    };

    place();
    const observer = new ResizeObserver(place);
    observer.observe(node);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [popover]);

  /**
   * A click outside dismisses the popover. Clicks inside are left alone: rows close it themselves
   * once their action has run, and unmounting the row mid-click would drop that action.
   */
  useEffect(() => {
    if (!popover) return;
    const onDocumentClick = (event: MouseEvent) => {
      const node = popoverRef.current;
      if (node && event.target instanceof Node && node.contains(event.target)) return;
      closePopover();
    };
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [closePopover, popover]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (popover) {
        closePopover();
        return;
      }
      if (modal) closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeModal, closePopover, modal, popover]);

  const value = useMemo<OverlayContextValue>(
    () => ({
      openModal,
      closeModal,
      openPopover,
      closePopover,
      isModalOpen: modal !== null,
      isPopoverOpen: popover !== null,
    }),
    [closeModal, closePopover, modal, openModal, openPopover, popover],
  );

  return (
    <OverlayContext.Provider value={value}>
      {children}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the scrim is a shortcut; Escape and the dialog close button are the accessible paths */}
      <div
        className={`rov${modal ? ' on' : ''}`}
        id="rov"
        onClick={(event) => {
          if (event.target === event.currentTarget) closeModal();
        }}
        role="presentation"
      >
        {modal}
      </div>
      <div className={`ov${popover ? ' on' : ''}`} id="ov" role="presentation" />
      {popover ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: the wrapper only stops the document-level dismiss listener
        <div
          className={`pop ${popover.className ?? ''}`}
          id="pop"
          onClick={(event) => event.stopPropagation()}
          ref={popoverRef}
          role="presentation"
          style={{
            left: position ? `${position.left}px` : '-9999px',
            top: position ? `${position.top}px` : '-9999px',
          }}
        >
          {popover.content}
        </div>
      ) : null}
    </OverlayContext.Provider>
  );
}
